
import React, { useState, useEffect } from 'react';
import { MapContainer, TileLayer, Marker, Popup, useMap } from 'react-leaflet';
import 'leaflet/dist/leaflet.css';
import { toast } from 'react-hot-toast';
import L from 'leaflet';
import { MapPin, Navigation } from 'lucide-react';
import { getSegmentColor } from '../../../utils/segmentUtils';

// Fix for default marker icons in Leaflet with webpack/vite
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: 'https://unpkg.com/leaflet@1.7.1/dist/images/marker-icon-2x.png',
  iconUrl: 'https://unpkg.com/leaflet@1.7.1/dist/images/marker-icon.png',
  shadowUrl: 'https://unpkg.com/leaflet@1.7.1/dist/images/marker-shadow.png',
});

// Create custom marker icons for each segment
const createCustomIcon = (color) => {
  return L.divIcon({
    className: 'custom-marker-icon',
    html: `<div style="background-color: ${color}; width: 24px; height: 24px; border-radius: 50%; border: 2px solid white; box-shadow: 0 2px 5px rgba(0, 0, 0, 0.3);"></div>`,
    iconSize: [24, 24],
    iconAnchor: [12, 12],
    popupAnchor: [0, -12],
  });
};

// Map view adapter component to update the view when stations change
const MapViewAdapter = ({ center, zoom }) => {
  const map = useMap();
  
  useEffect(() => {
    if (center && Array.isArray(center) && center.length === 2 && 
        typeof center[0] === 'number' && typeof center[1] === 'number' && 
        zoom) {
      map.setView(center, zoom);
    }
  }, [center, zoom, map]);
  
  return null;
};

const segmentColors = {
  urban: '#8B5CF6', // purple
  highway: '#3B82F6', // blue
  rural: '#10B981', // green
  suburban: '#F59E0B', // amber
};

const StationMapView = ({ segmentId = 'urban', stations = [] }) => {
  // Default center coordinates if no valid stations (Bangkok)
  const defaultCenter = [13.7563, 100.5018];
  
  const [mapCenter, setMapCenter] = useState(defaultCenter);
  const [mapZoom, setMapZoom] = useState(10);
  const [mapLoaded, setMapLoaded] = useState(false);
  const [selectedMarker, setSelectedMarker] = useState(null);
  
  // Filter out invalid stations (no coordinates)
  const validStations = Array.isArray(stations) ? 
    stations.filter(station => 
      station && 
      typeof station === 'object' && 
      typeof station.lat === 'number' && 
      typeof station.lng === 'number'
    ) : [];
  
  // Get segment color using the utility function
  const segmentColor = segmentColors[segmentId] || segmentColors.urban;
  
  // Create marker icon based on segment
  const markerIcon = createCustomIcon(segmentColor);
  
  // Adjust map view when stations change
  useEffect(() => {
    if (validStations.length > 0 && mapLoaded) {
      if (validStations.length === 1) {
        // If only one station, center on it
        setMapCenter([validStations[0].lat, validStations[0].lng]);
        setMapZoom(13);
      } else {
        // Calculate bounds to fit all stations
        const latitudes = validStations.map(s => s.lat);
        const longitudes = validStations.map(s => s.lng);
        
        const minLat = Math.min(...latitudes);
        const maxLat = Math.max(...latitudes);
        const minLng = Math.min(...longitudes);
        const maxLng = Math.max(...longitudes);
        
        // Center of the bounds
        const centerLat = (minLat + maxLat) / 2;
        const centerLng = (minLng + maxLng) / 2;
        
        setMapCenter([centerLat, centerLng]);
        
        // Set zoom based on bounds size (simplified approach)
        const latDiff = maxLat - minLat;
        const lngDiff = maxLng - minLng;
        const maxDiff = Math.max(latDiff, lngDiff);
        
        if (maxDiff > 3) setMapZoom(7);
        else if (maxDiff > 1) setMapZoom(8);
        else if (maxDiff > 0.5) setMapZoom(9);
        else if (maxDiff > 0.1) setMapZoom(10);
        else setMapZoom(12);
      }
    }
  }, [validStations, mapLoaded]);
  
  const handleMarkerClick = (station) => {
    setSelectedMarker(station);
    toast.success(`${station.name} selected`);
  };

  // Function to fit all stations
  const handleFitAllStations = () => {
    if (mapLoaded && validStations.length > 0) {
      // Calculate bounds to fit all stations
      const latitudes = validStations.map(s => s.lat);
      const longitudes = validStations.map(s => s.lng);
      
      const minLat = Math.min(...latitudes);
      const maxLat = Math.max(...latitudes);
      const minLng = Math.min(...longitudes);
      const maxLng = Math.max(...longitudes);
      
      // Center of the bounds
      const centerLat = (minLat + maxLat) / 2;
      const centerLng = (minLng + maxLng) / 2;
      
      setMapCenter([centerLat, centerLng]);
      
      // Set zoom based on bounds size
      const latDiff = maxLat - minLat;
      const lngDiff = maxLng - minLng;
      const maxDiff = Math.max(latDiff, lngDiff);
      
      if (maxDiff > 3) setMapZoom(7);
      else if (maxDiff > 1) setMapZoom(8);
      else if (maxDiff > 0.5) setMapZoom(9);
      else if (maxDiff > 0.1) setMapZoom(10);
      else setMapZoom(12);
    }
  };

  return (
    <div className="bg-white rounded-lg shadow mb-6">
      <div className="border-b border-gray-200 p-4 flex justify-between items-center">
        <h3 className="text-lg font-medium text-gray-900 flex items-center">
          <MapPin size={20} className="mr-2 text-purple-600" />
          {validStations.length} {validStations.length === 1 ? 'Station' : 'Stations'} in Map View
        </h3>
        
        {validStations.length > 0 && (
          <div className="flex space-x-2">
            <button 
              className="px-3 py-1.5 bg-white border border-gray-300 rounded-md text-sm text-gray-700 flex items-center hover:bg-gray-50"
              onClick={handleFitAllStations}
            >
              <Navigation size={14} className="mr-1" />
              Fit All Stations
            </button>
          </div>
        )}
      </div>
      
      <div className="p-4">
        <div className="h-[500px] w-full rounded-lg overflow-hidden border border-gray-200">
          <MapContainer 
            center={mapCenter} 
            zoom={mapZoom} 
            style={{ height: '100%', width: '100%' }}
            whenReady={() => setMapLoaded(true)}
          >
            <MapViewAdapter center={mapCenter} zoom={mapZoom} />
            
            <TileLayer
              attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
              url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
            />
            
            {validStations.map(station => (
              <Marker 
                key={station.id}
                position={[station.lat, station.lng]}
                icon={markerIcon}
                eventHandlers={{
                  click: () => handleMarkerClick(station),
                }}
              >
                <Popup>
                  <div className="p-1">
                    <h3 className="font-medium">{station.name}</h3>
                    <p className="text-sm text-gray-500">{station.id}</p>
                    <div className="mt-2 text-sm">
                      <p><span className="font-medium">Volume:</span> {station.volume.toLocaleString()} L/day</p>
                      <p><span className="font-medium">Premium:</span> {station.premium}%</p>
                      <p><span className="font-medium">Café:</span> {station.cafe.toLocaleString()} ฿/day</p>
                      <p><span className="font-medium">CLV:</span> {station.clv}x</p>
                      <p><span className="font-medium">Status:</span> <span className={station.status === 'above' ? 'text-green-600' : 'text-red-500'}>
                        {station.status === 'above' ? 'Above Target' : 'Below Target'}
                      </span></p>
                    </div>
                    
                    <div className="mt-2 pt-2 border-t border-gray-200">
                      <button 
                        className="w-full px-2 py-1 bg-purple-600 text-white text-xs rounded-md hover:bg-purple-700"
                        onClick={() => toast.success(`Details for ${station.name} requested`)}
                      >
                        View Detailed Analysis
                      </button>
                    </div>
                  </div>
                </Popup>
              </Marker>
            ))}
          </MapContainer>
        </div>
        
        {selectedMarker && (
          <div className="mt-4 p-3 border border-gray-200 rounded-lg bg-gray-50">
            <div className="flex justify-between items-start mb-2">
              <h4 className="font-medium text-gray-900">{selectedMarker.name}</h4>
              <span className="text-xs bg-purple-100 text-purple-800 px-2 py-0.5 rounded-full">
                {selectedMarker.id}
              </span>
            </div>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-sm">
              <div className="bg-white p-2 rounded border border-gray-200">
                <p className="text-gray-500">Volume</p>
                <p className="font-medium">{selectedMarker.volume.toLocaleString()} L/day</p>
              </div>
              <div className="bg-white p-2 rounded border border-gray-200">
                <p className="text-gray-500">Premium %</p>
                <p className="font-medium">{selectedMarker.premium}%</p>
              </div>
              <div className="bg-white p-2 rounded border border-gray-200">
                <p className="text-gray-500">Café Sales</p>
                <p className="font-medium">{selectedMarker.cafe} cups/day</p>
              </div>
              <div className="bg-white p-2 rounded border border-gray-200">
                <p className="text-gray-500">Customer Loyalty</p>
                <p className="font-medium">{selectedMarker.loyalty}%</p>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default StationMapView;
